import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PromptSearchResult } from 'app/shared/models/prompt-search-result';
import { PromptCopyService } from 'app/shared/services/prompt-copy.service';
import Fuse, { FuseResultMatch, IFuseOptions } from 'fuse.js';
import { debounceTime, distinctUntilChanged, map, of, startWith } from 'rxjs';
import { FuseAlertComponent, FuseAlertType } from '../../../../@fuse/components/alert';
import { Directory } from '../../../shared/models/directory';
import { ExcerptPipe } from '../../../shared/pipes/excerpt.pipe';
import { HighlightPipe } from '../../../shared/pipes/highlight.pipe';
import { MarkdownPipe } from '../../../shared/pipes/markdown.pipe';
import { DirectoryService } from '../../../shared/services/directory.service';
import { PromptService } from '../../../shared/services/prompt.service';
import { TenantRouterService } from '../../../shared/services/tenant-router.service';

const MAX_RESULTS = 20;
const FUSE_OPTIONS: IFuseOptions<PromptSearchResult> = {
    keys: [
        { name: 'prompt.name', weight: 3 },
        { name: 'prompt.text', weight: 1 },
        { name: 'directory.name', weight: 0.5 },
    ],
    minMatchCharLength: 2,
    ignoreLocation: true,
    threshold: 0.25,
    ignoreFieldNorm: true,
    includeMatches: true,
};

type SearchResultItem = {
    item: PromptSearchResult;
    matches?: readonly FuseResultMatch[];
};

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        ExcerptPipe,
        MarkdownPipe,
        HighlightPipe,
        FuseAlertComponent,
    ],
})
export class SearchComponent {
    private readonly promptService = inject(PromptService);
    private readonly promptCopyService = inject(PromptCopyService);
    private readonly directoryService = inject(DirectoryService);
    private readonly tenantRouterService = inject(TenantRouterService);
    private readonly triggerOrigin = viewChild(CdkOverlayOrigin);
    protected readonly searchControl = new FormControl<string>('');
    protected readonly panelOpen = signal(false);
    protected readonly overlayWidth = signal<number>(0);

    constructor() {
        effect((onCleanup) => {
            const element = this.triggerOrigin()?.elementRef.nativeElement;
            if (!element) return;
            this.overlayWidth.set(element.getBoundingClientRect().width);
            // Keep initial sizing even when ResizeObserver is unavailable or fires later.
            if (typeof ResizeObserver === 'undefined') {
                return;
            }

            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (entry) {
                    const borderBoxSize = Array.isArray(entry.borderBoxSize)
                        ? entry.borderBoxSize[0]
                        : entry.borderBoxSize;
                    this.overlayWidth.set(borderBoxSize?.inlineSize ?? entry.contentRect.width);
                }
            });
            observer.observe(element);
            onCleanup(() => observer.disconnect());
        });
    }

    protected alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    protected readonly showAlert = signal(false);
    protected readonly copyOnSelect = input(false);

    private readonly directoriesResource = rxResource({
        params: () => this.panelOpen(),
        stream: ({ params: open }) => {
            if (open) {
                return this.promptService
                    .getUserDirectoriesWithPrompts()
                    .pipe(
                        map((dirs) =>
                            dirs.flatMap(({ prompts = [], ...directory }) =>
                                prompts.map((prompt) => ({ prompt, directory }) as PromptSearchResult),
                            ),
                        ),
                    );
            } else {
                return of([]);
            }
        },
    });

    protected readonly searchQuery = toSignal(
        this.searchControl.valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged()),
        { initialValue: '' },
    );

    private readonly fuse = computed(() => {
        return new Fuse(this.directoriesResource.value() ?? [], FUSE_OPTIONS);
    });

    protected readonly searchResults = computed((): SearchResultItem[] => {
        const searchQuery = this.searchQuery()?.trim();

        if (!searchQuery) {
            return [];
        }

        return this.fuse().search(searchQuery, { limit: MAX_RESULTS });
    });

    protected async onSelect(result: SearchResultItem): Promise<void> {
        this.closePanel();

        if (this.copyOnSelect()) {
            const success = await this.promptCopyService.copyPrompt(result.item.prompt);
            if (success) {
                this.alert = {
                    type: 'success',
                    message: 'Prompt wurde erfolgreich in die Zwischenablage kopiert.',
                };
            } else {
                this.alert = {
                    type: 'error',
                    message: 'Kopieren fehlgeschlagen - bitte erneut versuchen.',
                };
            }

            this.showAlert.set(true);
            setTimeout(() => {
                this.showAlert.set(false);
            }, 3000);
        } else {
            this.tenantRouterService.navigate(['prompts', result.item.directory.uid, result.item.prompt.uid]);
        }

        this.searchControl.setValue('');
    }

    protected closePanel(): void {
        this.panelOpen.set(false);
    }

    protected clearSearch(): void {
        this.searchControl.setValue('');
    }

    protected getDirectoryIcon(directory: Directory): string {
        return this.directoryService.getDirectoryIcon(directory);
    }
}
